import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common";
import { TripStepper, ExpenseForm, ExpenseList, PodSection, StopsList } from "@/components/trip";
import { DriverShell } from "@/pages/DriverHome";

function Block({ title, children, testid }) {
  return <section data-testid={testid} className="card-base p-4 fade-up"><h3 className="mb-3 text-base font-semibold">{title}</h3>{children}</section>;
}

export default function DriverTrip() {
  const { id } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const load = useCallback(() => api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const back = <Link to="/driver" data-testid="driver-back-btn" className="flex items-center gap-2 text-sm font-semibold"><ArrowLeft className="h-5 w-5" /> My Trips</Link>;
  if (!b) return <DriverShell back={back}><div className="text-sm text-muted-foreground">Loading…</div></DriverShell>;
  return (
    <DriverShell back={back}>
      <section className="rounded-xl bg-slate-900 p-5 text-white dark:bg-card dark:border fade-up" data-testid="driver-trip-header">
        <div className="flex items-center justify-between"><span className="font-mono text-xs text-slate-300">{b.lr_number}</span><StatusBadge status={b.status} testid="driver-trip-status" /></div>
        <div className="mt-3 text-2xl font-bold leading-tight">{b.pickup_location}</div>
        <div className="text-2xl font-bold leading-tight text-sky-400">→ {b.drop_location}</div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-300">
          <div>Vehicle<div className="font-mono text-sm font-semibold text-white">{b.vehicle?.vehicle_number || "-"}</div></div>
          <div>Load<div className="text-sm font-semibold text-white">{b.material_type} · {b.weight_tons} T</div></div>
        </div>
        {b.consignee.phone && (
          <a href={`tel:${b.consignee.phone}`} data-testid="call-consignee-btn" className="mt-4 flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-white/10 text-sm font-semibold">
            <Phone className="h-4 w-4" /> Call Consignee · {b.consignee.name}
          </a>
        )}
      </section>
      <Block title="Loading & Unloading Points" testid="driver-stops-block"><StopsList booking={b} /></Block>
      <Block title="Trip Status" testid="driver-status-block"><TripStepper booking={b} onChange={load} big /></Block>
      <Block title="Add Fuel / Expense" testid="driver-expense-block"><ExpenseForm bookingId={b.id} onAdded={load} big /></Block>
      <Block title="Proof of Delivery" testid="driver-pod-block"><PodSection booking={b} onChange={load} big /></Block>
      <Block title="My Expenses on this Trip" testid="driver-expense-list"><ExpenseList expenses={b.expenses} onChange={load} canEdit={(e) => e.created_by === user?.id} /></Block>
    </DriverShell>
  );
}
